/**
 * lazyWithRetry — self-healing wrapper around React.lazy().
 *
 * Route chunks are fetched on demand. Two things make those fetches fail:
 *   1. A deploy replaced the build while the user's tab was open, so the
 *      hashed chunk filename the old index references no longer exists (404).
 *   2. A transient network / single-instance blip drops the request.
 *
 * Either way React throws "Failed to fetch dynamically imported module" and
 * the user hits the error boundary (SYS_001). This wrapper:
 *   - retries the import a couple of times with short backoff (covers blips),
 *   - then, if it's specifically a chunk-load failure, reloads the page ONCE
 *     so the browser pulls the current build's index + chunks (covers deploys),
 *   - guards that reload with sessionStorage so it can never loop: if we've
 *     already reloaded for this chunk and it still fails, we rethrow and let
 *     the error boundary handle it.
 *
 * Genuine component errors (the module throws while evaluating) are NOT
 * chunk-load errors, so they rethrow immediately without any reload.
 */
import { lazy, type ComponentType } from 'react';

type Factory<T extends ComponentType<any>> = () => Promise<{ default: T }>;

/** Regex for the chunk-load error across browsers (Chrome/FF/Safari wording). */
const CHUNK_LOAD_ERROR =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i;

function isChunkLoadError(error: unknown): boolean {
  const message =
    error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  return CHUNK_LOAD_ERROR.test(message);
}

/** Stable-ish hash of a string, used to derive a per-chunk key from the factory. */
function hashString(input: string): string {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = (hash << 5) - hash + input.charCodeAt(i);
    hash |= 0; // force 32-bit
  }
  return Math.abs(hash).toString(36);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Read/write the sessionStorage reload guard, swallowing storage errors. */
function getReloadFlag(storageKey: string): boolean {
  try {
    return window.sessionStorage.getItem(storageKey) === '1';
  } catch {
    return false;
  }
}
function setReloadFlag(storageKey: string): void {
  try {
    window.sessionStorage.setItem(storageKey, '1');
  } catch {
    /* ignore — private mode / storage disabled */
  }
}
function clearReloadFlag(storageKey: string): void {
  try {
    window.sessionStorage.removeItem(storageKey);
  } catch {
    /* ignore */
  }
}

// Backoff between import attempts (ms). Length also defines the retry count.
const RETRY_DELAYS = [400, 1000];

/**
 * Wrap a dynamic import factory with retry + one-time reload self-healing.
 *
 * @param factory The same `() => import('./Page')` you'd pass to React.lazy.
 * @param key     Optional stable key for the sessionStorage reload guard.
 *                Defaults to a hash of the factory source so each route gets
 *                its own flag. Pass an explicit key when factory sources could
 *                collide (e.g. minified identical one-liners).
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: Factory<T>,
  key?: string,
) {
  const chunkKey = key ?? hashString(factory.toString());
  const storageKey = `lazyReload:${chunkKey}`;

  return lazy(async () => {
    let lastError: unknown;
    // Initial attempt + one retry per backoff entry.
    for (let attempt = 0; attempt <= RETRY_DELAYS.length; attempt++) {
      try {
        const mod = await factory();
        // Loaded fine — clear any stale reload guard from a prior failure.
        clearReloadFlag(storageKey);
        return mod;
      } catch (error) {
        lastError = error;
        if (attempt < RETRY_DELAYS.length) {
          await sleep(RETRY_DELAYS[attempt]);
        }
      }
    }

    // All attempts failed. Only self-heal chunk-load errors; real
    // component errors must surface to the error boundary untouched.
    if (isChunkLoadError(lastError) && !getReloadFlag(storageKey)) {
      // First failure for this chunk: mark it and reload so the browser
      // fetches the CURRENT build. If the reload fixes it, the next load
      // clears the flag; if it STILL fails, the flag is set so we fall
      // through to the rethrow below and never reload a second time.
      setReloadFlag(storageKey);
      window.location.reload();
      // Reload is async; return a never-resolving promise so Suspense keeps
      // showing the fallback instead of flashing the error boundary first.
      return new Promise<{ default: T }>(() => {});
    }

    throw lastError;
  });
}

export default lazyWithRetry;
